import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { Button } from '@/components/atoms';
import { FrammingModal } from '@/components/FrammingModal/FrammingModal';
import { boardApi } from '@/services/board';
import { sitesApi } from '@/services/config';
import { useNavPulse } from '@/store/useNavPulse';
import {
  intelligenceService,
  type NewsCard,
  type TrendItem,
} from '@/services/intelligence';

import styles from './Intelligence.module.css';

const EDITORIAS = ['Esportes', 'Economia', 'Mundo', 'Tecnologia', 'Tempo', 'Entretenimento', 'Saúde', 'Política'];
const HOURS = [
  { v: 4, label: '4h' },
  { v: 24, label: '24h' },
  { v: 48, label: '48h' },
  { v: 168, label: '7 dias' },
];
// Geografia: Brasil (país) + os 27 estados via códigos ISO 3166-2.
const GEOS = [
  { v: 'BR', label: 'Brasil (país)' },
  { v: 'BR-AC', label: 'Acre' },
  { v: 'BR-AL', label: 'Alagoas' },
  { v: 'BR-AP', label: 'Amapá' },
  { v: 'BR-AM', label: 'Amazonas' },
  { v: 'BR-BA', label: 'Bahia' },
  { v: 'BR-CE', label: 'Ceará' },
  { v: 'BR-DF', label: 'Distrito Federal' },
  { v: 'BR-ES', label: 'Espírito Santo' },
  { v: 'BR-GO', label: 'Goiás' },
  { v: 'BR-MA', label: 'Maranhão' },
  { v: 'BR-MT', label: 'Mato Grosso' },
  { v: 'BR-MS', label: 'Mato Grosso do Sul' },
  { v: 'BR-MG', label: 'Minas Gerais' },
  { v: 'BR-PA', label: 'Pará' },
  { v: 'BR-PB', label: 'Paraíba' },
  { v: 'BR-PR', label: 'Paraná' },
  { v: 'BR-PE', label: 'Pernambuco' },
  { v: 'BR-PI', label: 'Piauí' },
  { v: 'BR-RJ', label: 'Rio de Janeiro' },
  { v: 'BR-RN', label: 'Rio Grande do Norte' },
  { v: 'BR-RS', label: 'Rio Grande do Sul' },
  { v: 'BR-RO', label: 'Rondônia' },
  { v: 'BR-RR', label: 'Roraima' },
  { v: 'BR-SC', label: 'Santa Catarina' },
  { v: 'BR-SP', label: 'São Paulo' },
  { v: 'BR-SE', label: 'Sergipe' },
  { v: 'BR-TO', label: 'Tocantins' },
];

const geoLabel = (v: string) => GEOS.find((g) => g.v === v)?.label ?? v;

function fmtVol(v: number | null): string {
  if (!v) return '—';
  if (v >= 1000) return `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)} mil`;
  return String(v);
}

function timeAgo(ts: number | null): string | null {
  if (!ts) return null;
  const diff = Date.now() / 1000 - ts;
  if (diff < 0) return 'agora';
  const h = Math.floor(diff / 3600);
  if (h < 1) return `há ${Math.max(1, Math.floor(diff / 60))} min`;
  if (h < 24) return `há ${h}h`;
  return `há ${Math.floor(h / 24)} d`;
}

function bandLabel(band: string): string {
  if (band === 'alta') return 'Ótimo tema';
  if (band === 'media') return 'Tema viável';
  return 'Tema fraco';
}

function flagLabel(flag: string): string {
  if (flag === 'generico') return 'termo genérico';
  if (flag === 'evergreen') return 'recorrente';
  if (flag === 'navegacional') return 'navegacional';
  return flag;
}

function scoreColor(score: number): string {
  if (score >= 70) return 'var(--ok)';
  if (score >= 45) return 'var(--warn)';
  return 'var(--text-4)';
}

function verdictTone(verdict: string): string {
  switch (verdict) {
    case 'FURO':
      return styles.vFuro;
    case 'COBRIR':
      return styles.vCobrir;
    case 'SATURADO':
      return styles.vSaturado;
    default:
      return styles.vIgnorar;
  }
}

export function Intelligence() {
  const [geo, setGeo] = useState('BR');
  const [hours, setHours] = useState(24);
  const [category, setCategory] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [selected, setSelected] = useState<TrendItem | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);

  const trendsQuery = useQuery({
    queryKey: ['intel', 'trends', geo, hours, category, onlyActive],
    queryFn: () => intelligenceService.getTrends({ geo, hours, category, onlyActive }),
  });

  // Temas já PUBLICADOS (coluna Publicado do quadro) → badge "publicado" nos cards.
  const boardQuery = useQuery({
    queryKey: ['board', {}],
    queryFn: () => boardApi.get({}),
    staleTime: 60_000,
  });
  const publishedSet = useMemo(() => {
    const s = new Set<string>();
    for (const c of boardQuery.data?.columns.publicado ?? []) {
      if (c.tema) s.add(c.tema.toLowerCase().trim());
    }
    return s;
  }, [boardQuery.data]);

  const items = trendsQuery.data?.items ?? [];

  return (
    <section className={styles.root}>
      <header className={styles.head}>
        <div>
          <h1 className={styles.title}>Inteligência · Tendências</h1>
          <p className={styles.subtitle}>
            O que o Brasil procura agora, ranqueado por relevância editorial.
            Cada card já traz tudo da tendência — abre para a cobertura e as ações.
          </p>
        </div>
      </header>

      {feedback && (
        <p className={styles.flash}>
          <span onClick={() => setFeedback(null)} style={{ cursor: 'pointer' }}>{feedback}</span>
          {feedback.includes('quadro') && (
            <>
              {' '}
              <Link to="/producao" style={{ marginLeft: 8, fontWeight: 500 }}>
                ver no quadro →
              </Link>
            </>
          )}
        </p>
      )}

      {/* ─── Filtros ─── */}
      <div className={styles.filters}>
        <label className={styles.filter}>
          <span>Geografia</span>
          <select value={geo} onChange={(e) => setGeo(e.target.value)}>
            {GEOS.map((g) => (
              <option key={g.v} value={g.v}>
                {g.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.filter}>
          <span>Janela</span>
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))}>
            {HOURS.map((h) => (
              <option key={h.v} value={h.v}>
                {h.label}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.filter}>
          <span>Editoria</span>
          <select value={category} onChange={(e) => setCategory(e.target.value)}>
            <option value="">Todas</option>
            {EDITORIAS.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={onlyActive}
            onChange={(e) => setOnlyActive(e.target.checked)}
          />
          <span>só ativas</span>
        </label>
        <div className={styles.filterSpacer} />
        <span className={styles.count}>
          {trendsQuery.isFetching ? 'a carregar…' : `${items.length} tendências`}
        </span>
        <Button size="sm" variant="secondary" onClick={() => trendsQuery.refetch()}>
          Atualizar
        </Button>
      </div>

      {trendsQuery.isError && (
        <p className={styles.error}>
          Falha ao carregar tendências. Confirma a chave SerpApi em /config/credenciais.
        </p>
      )}

      {/* ─── Grelha de cards ─── */}
      <div className={styles.cards}>
        {items.map((t) => (
          <TrendCard
            key={t.query}
            t={t}
            geo={geo}
            published={publishedSet.has(t.query.toLowerCase().trim())}
            onOpen={() => { setSelected(t); setFeedback(null); }}
            onProduced={(msg) => setFeedback(msg)}
          />
        ))}
      </div>
      {!trendsQuery.isLoading && items.length === 0 && !trendsQuery.isError && (
        <p className={styles.empty}>Nenhuma tendência com estes filtros.</p>
      )}

      {/* ─── Modal denso ─── */}
      {selected && (
        <TopicModal
          item={selected}
          onClose={() => setSelected(null)}
          onFeedback={(m) => { setFeedback(m); setSelected(null); }}
        />
      )}
    </section>
  );
}

// ─── Card de tendência ───────────────────────────────────────────────────────

function TrendCard({
  t,
  geo,
  published,
  onOpen,
  onProduced,
}: {
  t: TrendItem;
  geo: string;
  published?: boolean;
  onOpen: () => void;
  onProduced?: (msg: string) => void;
}) {
  const ago = timeAgo(t.start_timestamp);
  const furo = t.score >= 55 && t.active && t.confianca >= 60 && t.flags.length === 0;
  // Handoff leve direto do card (sem abrir o modal): manda o tema pra Produção
  // pra outra pessoa ver ângulo/editar. `intel` é opcional no enqueue.
  const produzir = useMutation({
    mutationFn: () => boardApi.enqueue({ tema: t.query, fonte: 'inteligencia' }),
    onSuccess: (card) => {
      onProduced?.(
        card.duplicado
          ? `"${t.query}" já estava no quadro de Produção.`
          : `"${t.query}" enviado para Produção.`,
      );
      useNavPulse.getState().pulseProducao();
    },
  });
  return (
    <div className={styles.card}>
      <button type="button" className={styles.cardBody} onClick={onOpen}>
      <div className={styles.cardTop}>
        <span className={styles.edBadge}>{t.editoria ?? 'Geral'}</span>
        {published && <span className={styles.pubBadge}>✓ publicado</span>}
        <span className={t.active ? styles.dirUp : styles.dirDown}>
          {t.active ? '▲ em alta' : '▼ em declínio'}
          {ago && t.active ? ` · ${ago}` : ''}
        </span>
      </div>

      <h3 className={styles.cardTitle}>{t.query}</h3>

      <div className={styles.cardMetrics}>
        <div className={styles.metric}>
          <span className={styles.metricVal} style={{ color: scoreColor(t.score) }}>{t.score}</span>
          <span className={styles.metricLbl}>score</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricVal}>{fmtVol(t.search_volume)}</span>
          <span className={styles.metricLbl}>buscas</span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricVal}>{t.increase_percentage ? `+${t.increase_percentage}%` : '—'}</span>
          <span className={styles.metricLbl}>variação</span>
        </div>
      </div>

      {/* mini-gráfico: decomposição do score (sem nova chamada) */}
      <div className={styles.bars}>
        <Bar label="audiência" v={t.audiencia} />
        <Bar label="momentum" v={t.momentum} />
        <Bar label="fit" v={t.fit} />
      </div>

      <div className={styles.cardFoot}>
        <span className={`${styles.goodTag} ${styles[`band-${t.band}`]}`}>{bandLabel(t.band)}</span>
        {furo && <span className={styles.furoTag}>potencial furo</span>}
        {t.flags.map((f) => (
          <span key={f} className={styles.flagTag}>{flagLabel(f)}</span>
        ))}
        <span className={styles.geoTag}>{geoLabel(geo)}</span>
      </div>

      {t.trend_breakdown.length > 0 && (
        <div className={styles.cardChips}>
          {t.trend_breakdown.slice(0, 3).map((b) => (
            <span key={b} className={styles.chip}>{b}</span>
          ))}
        </div>
      )}
      </button>
      <button
        type="button"
        className={styles.produzirBtn}
        disabled={produzir.isPending}
        onClick={() => produzir.mutate()}
      >
        {produzir.isPending ? 'Enviando…' : 'Produzir →'}
      </button>
    </div>
  );
}

function Bar({ label, v }: { label: string; v: number }) {
  return (
    <div className={styles.barRow}>
      <span className={styles.barLbl}>{label}</span>
      <span className={styles.barTrack}>
        <span className={styles.barFill} style={{ width: `${v}%` }} />
      </span>
      <span className={styles.barNum}>{v}</span>
    </div>
  );
}

// ─── Modal denso ─────────────────────────────────────────────────────────────

function TopicModal({
  item,
  onClose,
  onFeedback,
}: {
  item: TrendItem;
  onClose: () => void;
  onFeedback: (m: string) => void;
}) {
  const [editoria, setEditoria] = useState(item.editoria ?? '');
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState('');
  const [frammingOpen, setFrammingOpen] = useState(false);
  const [destino, setDestino] = useState(''); // E5-01: destino opcional já na curadoria
  const sitesQuery = useQuery({
    queryKey: ['wp-sites', 'active'],
    queryFn: () => sitesApi.list(true),
    staleTime: 300_000,
  });

  const topicQuery = useQuery({
    queryKey: ['intel', 'topic', item.query],
    queryFn: () =>
      intelligenceService.getTopic({
        query: item.query,
        search_volume: item.search_volume,
        increase_percentage: item.increase_percentage,
        editoria: item.editoria,
      }),
  });

  useEffect(() => {
    if (topicQuery.data && !editoria && topicQuery.data.editoria) {
      setEditoria(topicQuery.data.editoria);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicQuery.data]);

  const detail = topicQuery.data;

  const enviarQuadro = useMutation({
    mutationFn: () =>
      boardApi.enqueue({
        tema: item.query,
        fonte: 'inteligencia',
        site: destino || undefined, // E5-01: destino já viaja (opcional)
        // leva o TopicDetail COMPLETO (cobertura/PAA/keywords) — handoff curadoria→produção
        intel: detail ? { ...detail, editoria: editoria || detail.editoria } : undefined,
      }),
    onSuccess: (card) => {
      onFeedback(
        card.duplicado
          ? `"${item.query}" já estava no quadro de Produção.`
          : `"${item.query}" enviado para o quadro de Produção.`,
      );
      useNavPulse.getState().pulseProducao(); // pisca a aba Produção
      onClose();
    },
  });

  const grouped = useMemo(() => {
    const map = new Map<string, NewsCard[]>();
    for (const n of detail?.news ?? []) {
      const k = n.source ?? 'Outros';
      if (!map.has(k)) map.set(k, []);
      map.get(k)!.push(n);
    }
    return [...map.entries()].sort((a, b) => b[1].length - a[1].length);
  }, [detail?.news]);

  const addTag = () => {
    const v = tagInput.trim();
    if (v && !tags.includes(v)) setTags([...tags, v]);
    setTagInput('');
  };

  return (
    <div className={styles.modalBackdrop} onClick={onClose}>
      <aside className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.modalHead}>
          <div>
            <h2 className={styles.modalTitle}>{item.query}</h2>
            <span className={styles.modalSub}>
              {item.active ? '▲ em alta' : '▼ em declínio'} ·{' '}
              {fmtVol(item.search_volume)} buscas ·{' '}
              {item.increase_percentage ? `+${item.increase_percentage}%` : 'sem variação'}
            </span>
          </div>
          <button className={styles.modalClose} onClick={onClose}>fechar</button>
        </header>

        {topicQuery.isLoading && <p className={styles.muted}>A analisar cobertura e perguntas…</p>}
        {topicQuery.isError && <p className={styles.error}>Falha ao detalhar o tópico.</p>}

        {detail && (
          <div className={styles.modalBody}>
            {/* coluna esquerda: contexto + cobertura */}
            <div className={styles.colMain}>
              <div className={styles.verdictRow}>
                <span className={`${styles.verdict} ${verdictTone(detail.verdict)}`}>
                  {detail.verdict}
                </span>
                <span className={styles.verdictMotivo}>{detail.motivo}</span>
              </div>

              <div className={styles.section}>
                <span className={styles.sectionLabel}>
                  Cobertura por veículo · {grouped.length} veículos · {detail.news.length} matérias
                </span>
                {grouped.length > 0 ? (
                  <div className={styles.groups}>
                    {grouped.map(([source, arts]) => (
                      <div key={source} className={styles.group}>
                        <div className={styles.groupHead}>
                          {arts[0]?.source_icon && (
                            <img className={styles.newsIcon} src={arts[0].source_icon} alt="" />
                          )}
                          <span className={styles.groupName}>{source}</span>
                          <span className={styles.groupCount}>{arts.length}</span>
                        </div>
                        <ul className={styles.groupList}>
                          {arts.map((n, i) => (
                            <li key={n.link ?? `${n.title}-${i}`} className={styles.groupItem}>
                              {n.link ? (
                                <a href={n.link} target="_blank" rel="noopener noreferrer">
                                  {n.title}
                                </a>
                              ) : (
                                <span>{n.title}</span>
                              )}
                              {n.date && <span className={styles.newsDate}> · {n.date}</span>}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className={styles.muted}>Sem cobertura detetada — forte sinal de furo.</p>
                )}
              </div>

              <div className={styles.section}>
                <span className={styles.sectionLabel}>People Also Ask</span>
                <p className={styles.paaPitch}>
                  Estas perguntas são as dúvidas do público e podem gerar engajamento
                  em todos os nossos veículos.
                </p>
                {detail.paa.length > 0 ? (
                  <ul className={styles.paa}>
                    {detail.paa.map((q) => (
                      <li key={q}>{q}</li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.muted}>Sem PAA para este termo (típico de nomes próprios).</p>
                )}
              </div>

              {detail.keywords.length > 0 && (
                <div className={styles.section}>
                  <span className={styles.sectionLabel}>
                    Palavras-chave — buscas reais do público (SEO)
                  </span>
                  <p className={styles.muted}>
                    Vão direto para o escritor de matérias. Clica para também usar como tag.
                  </p>
                  <div className={styles.cardChips}>
                    {detail.keywords.map((k) => (
                      <button
                        key={k}
                        type="button"
                        className={styles.suggestChip}
                        onClick={() => !tags.includes(k) && setTags([...tags, k])}
                      >
                        + {k}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* coluna direita: edição + ações */}
            <aside className={styles.colSide}>
              <div className={styles.sideCard}>
                <span className={styles.sideLabel}>Editoria</span>
                <select
                  className={styles.sideSelect}
                  value={editoria}
                  onChange={(e) => setEditoria(e.target.value)}
                >
                  <option value="">— escolher —</option>
                  {EDITORIAS.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>

              <div className={styles.sideCard}>
                <span className={styles.sideLabel}>Tags</span>
                <div className={styles.tagRow}>
                  <input
                    className={styles.tagInput}
                    value={tagInput}
                    placeholder="adicionar tag…"
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); addTag(); }
                    }}
                  />
                  <Button size="sm" variant="secondary" onClick={addTag}>+</Button>
                </div>
                {tags.length > 0 && (
                  <div className={styles.tagChips}>
                    {tags.map((tg) => (
                      <span key={tg} className={styles.tagChip} onClick={() => setTags(tags.filter((x) => x !== tg))}>
                        {tg} ×
                      </span>
                    ))}
                  </div>
                )}
                {item.trend_breakdown.length > 0 && (
                  <div className={styles.tagSuggest}>
                    <span className={styles.muted}>sugestões:</span>
                    {item.trend_breakdown.map((b) => (
                      <button
                        key={b}
                        type="button"
                        className={styles.suggestChip}
                        onClick={() => !tags.includes(b) && setTags([...tags, b])}
                      >
                        + {b}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className={styles.sideActions}>
                <Button onClick={() => setFrammingOpen(true)}>
                  Gerar só este assunto (com ângulo)
                </Button>
                <select
                  value={destino}
                  onChange={(e) => setDestino(e.target.value)}
                  style={{ fontSize: 13, padding: '4px 8px' }}
                  title="Destino opcional — quem produzir pode escolher depois"
                >
                  <option value="">destino (o produtor escolhe)</option>
                  {(sitesQuery.data ?? []).map((s) => (
                    <option key={s.slug} value={s.slug}>{s.name}</option>
                  ))}
                </select>
                <Button
                  variant="secondary"
                  disabled={enviarQuadro.isPending}
                  onClick={() => enviarQuadro.mutate()}
                >
                  Enviar para o quadro de Produção
                </Button>
              </div>
              <p className={styles.hint}>
                O conteúdo é redigido a partir do PAA + cobertura + dados da tendência,
                com prompt travado. Você escolhe o destino e o ângulo; sai como rascunho
                no WordPress.
              </p>
            </aside>
          </div>
        )}
      </aside>

      <FrammingModal
        open={frammingOpen}
        onClose={() => setFrammingOpen(false)}
        sourceType="trends"
        tema={item.query}
        payload={{ query: item.query, editoria: editoria || item.editoria || undefined, tags }}
        onGenerated={(r) =>
          onFeedback(
            r.wp_link
              ? `Rascunho gerado com ângulo: "${r.title}" (#${r.wp_id}).`
              : `Matéria gerada: "${r.title}".`,
          )
        }
      />
    </div>
  );
}
